import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { PilotTrackingTagMissionSpec } from "@nova/schemas"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"

const invalidateTags = () =>
    Promise.all([
        mutate((key) => Array.isArray(key) && key[0] === "tags"),
        mutate((key) => Array.isArray(key) && key[0] === "mission-table"),
        invalidatePilotTrackingQuery("tags"),
        invalidatePilotTrackingQuery("missions"),
    ])

const fetchTags = (orgId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/tags", { params: { orgId } })
}

export const useTags = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(["tags", orgId], () =>
        orgId ? fetchTags(orgId) : undefined
    )

    return { data, isLoading }
}

const createTag = (orgId: string, body: { name: string }) => {
    return fetchApiGateway("post /org/:orgId/fleet/tag", { body, params: { orgId } })
}

export const useCreateTag = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-tag",
        (_key, req: { arg: { name: string } }) => (orgId ? createTag(orgId, req.arg) : undefined),
        { onSuccess: invalidateTags }
    )

    return { createTag: trigger, isMutating }
}

const updateTag = (orgId: string, tagId: string, body: { name: string }) => {
    return fetchApiGateway("patch /org/:orgId/fleet/tag/:tagId", { body, params: { orgId, tagId } })
}

export const useUpdateTag = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-tag",
        (_key, req: { arg: { tagId: string; name: string } }) =>
            orgId ? updateTag(orgId, req.arg.tagId, { name: req.arg.name }) : undefined,
        { onSuccess: invalidateTags }
    )

    return { updateTag: trigger, isMutating }
}

const deleteTag = (orgId: string, tagId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/tag/:tagId", { params: { orgId, tagId } })
}

export const useDeleteTag = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-tag",
        (_key, req: { arg: { tagId: string } }) =>
            orgId ? deleteTag(orgId, req.arg.tagId) : undefined,
        { onSuccess: invalidateTags }
    )

    return { deleteTag: trigger, isMutating }
}

const batchUpdateTags = (
    orgId: string,
    body: { additions: PilotTrackingTagMissionSpec[]; deletions: PilotTrackingTagMissionSpec[] }
) => {
    return fetchApiGateway("post /org/:orgId/fleet/tag/batch", {
        body: body,
        params: { orgId },
    })
}

export const useBatchTagMissions = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "batch-tag-missions",
        (
            key,
            req: {
                arg: {
                    additions: PilotTrackingTagMissionSpec[]
                    deletions: PilotTrackingTagMissionSpec[]
                }
            }
        ) => (orgId ? batchUpdateTags(orgId, req.arg) : undefined),
        { onSuccess: invalidateTags }
    )

    return { batchTagMissions: trigger, isMutating }
}
